/**
 * ButtonPulse — [data-hover-color] 要素の hover/focus で内部 icon を WAAPI snap pulse (#650)。
 *
 * CLAUDE.md は「data-component 必須」 だが、 trigger が 17+ callsite に分散する
 * 軽量挙動 (per-element state は WeakMap で十分) のため、 body 上の event
 * delegation を採用 (link-hover-color.js と同パターン)。 listener 数は要素数に
 * 関わらず 4 個固定でメモリ効率が良い。
 *
 * trigger / icon selector は `TRIGGER_SELECTOR` / `ICON_SELECTOR` で集中管理。
 * 子要素間移動 (icon ↔ text 等) で再発火する mouseenter/mouseleave は
 * relatedTarget guard で無視。
 */

/** mouseleave/blur 時の smooth wind-down 時間 (ms) */
const SETTLE_MS = 220;
const SETTLE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const TRIGGER_SELECTOR = '[data-hover-color]';
/** pulse 対象 icon の selector (拡張時はここに追加) */
const ICON_SELECTOR = '.u-btn__icon, .c-page-career-jobs__card-button';

/**
 * @typedef {Object} PulseState
 * @property {HTMLElement} icon
 * @property {Animation | null} animation
 * @property {number | null} settleTimer
 * @property {boolean} hover
 * @property {boolean} focused
 */

/** @type {WeakMap<HTMLElement, PulseState>} */
const registry = new WeakMap();

/** @type {MediaQueryList} */
let desktopMql;
/** @type {MediaQueryList} */
let reduceMotionMql;

/**
 * trigger 要素から state を取得 (初回は作成)。 icon 不在の要素は null を返す。
 * @param {HTMLElement} btn
 * @returns {PulseState | null}
 */
function getState(btn) {
  const icon = /** @type {HTMLElement | null} */ (
    btn.querySelector(ICON_SELECTOR)
  );
  if (!icon) return null;
  let state = registry.get(btn);
  if (!state) {
    state = {
      icon,
      animation: null,
      settleTimer: null,
      hover: false,
      focused: false,
    };
    registry.set(btn, state);
  }
  return state;
}

/**
 * hover || focused の OR から play / settle を決める。 desktop 幅外 / reduce-motion では no-op。
 * @param {PulseState} state
 */
function updatePlayback(state) {
  if (!desktopMql.matches) return;
  if (reduceMotionMql.matches) return;
  const active = state.hover || state.focused;
  if (active && !state.animation) {
    playPulse(state);
  } else if (!active && state.animation) {
    settlePulse(state);
  }
}

/** @param {PulseState} state */
function playPulse(state) {
  if (state.settleTimer != null) {
    clearTimeout(state.settleTimer);
    state.settleTimer = null;
  }
  if (state.animation) state.animation.cancel();
  state.icon.style.transform = '';
  state.icon.style.transition = '';

  // fallback (0.9 / 1000ms) は .u-btn__icon の CSS default と一致
  const computed = getComputedStyle(state.icon);
  const pulseScale =
    parseFloat(computed.getPropertyValue('--_pulse-scale')) || 0.9;
  const durationStr = computed.getPropertyValue('--_pulse-duration').trim();
  let durationMs = 1000;
  if (durationStr) {
    const num = parseFloat(durationStr);
    durationMs = durationStr.endsWith('ms') ? num : num * 1000;
  }

  const anim = state.icon.animate(
    [
      { transform: `scale(${pulseScale})`, offset: 0, easing: 'steps(1, end)' },
      { transform: 'scale(1)', offset: 0.25, easing: 'steps(1, end)' },
      {
        transform: `scale(${pulseScale})`,
        offset: 0.5,
        easing: 'steps(1, end)',
      },
      { transform: 'scale(1)', offset: 0.75, easing: 'steps(1, end)' },
      { transform: 'scale(1)', offset: 1 },
    ],
    { duration: durationMs, iterations: 1 },
  );
  anim.onfinish = () => {
    if (state.animation === anim) state.animation = null;
  };
  state.animation = anim;
}

/** @param {PulseState} state */
function settlePulse(state) {
  if (!state.animation) return;
  // cancel 前に animated 値を inline へ固定 (cancel 後だと base へ戻り snap が起きる)
  const current = getComputedStyle(state.icon).transform;
  state.animation.cancel();
  state.animation = null;
  state.icon.style.transition = 'none';
  state.icon.style.transform = current === 'none' ? 'scale(1)' : current;
  // 強制 reflow して次の style 変更を transition 対象に
  void state.icon.offsetHeight;
  state.icon.style.transition = `transform ${SETTLE_MS}ms ${SETTLE_EASE}`;
  state.icon.style.transform = 'scale(1)';
  state.settleTimer = window.setTimeout(() => {
    state.icon.style.transform = '';
    state.icon.style.transition = '';
    state.settleTimer = null;
  }, SETTLE_MS + 30);
}

/**
 * event から trigger 要素 (= 最寄りの [data-hover-color]) を解決。
 * 子要素間移動 (relatedTarget が同じ trigger 内) は null を返して呼び出し側で skip。
 * @param {Event} e
 * @returns {HTMLElement | null}
 */
function resolveTrigger(e) {
  const target = /** @type {Element} */ (e.target);
  const btn = /** @type {HTMLElement | null} */ (
    target.closest?.(TRIGGER_SELECTOR)
  );
  if (!btn) return null;
  // mouseenter/mouseleave は子要素間移動でも capture phase で発火するため、
  // relatedTarget が同じ trigger 内のときは無視する (focus/blur は relatedTarget 未参照で OK)
  if (
    e instanceof MouseEvent &&
    btn.contains(/** @type {Node | null} */ (e.relatedTarget))
  ) {
    return null;
  }
  return btn;
}

/**
 * 1 つの event type に対し、 (state, btn) の操作を bind して body capture listener を登録。
 * @param {string} type
 * @param {(state: PulseState, btn: HTMLElement) => void} handler
 */
function bind(type, handler) {
  document.body.addEventListener(
    type,
    /** @param {Event} e */ e => {
      const btn = resolveTrigger(e);
      if (!btn) return;
      const state = getState(btn);
      if (!state) return;
      handler(state, btn);
    },
    true,
  );
}

/**
 * body 上の delegation で hover/focus イベントを処理。
 * mouseenter/focus は bubble しないので capture phase で取得。
 */
export function initButtonPulse() {
  desktopMql = window.matchMedia('(width >= 770px)');
  reduceMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');

  bind('mouseenter', state => {
    state.hover = true;
    updatePlayback(state);
  });
  bind('mouseleave', state => {
    state.hover = false;
    updatePlayback(state);
  });
  bind('focus', (state, btn) => {
    if (!btn.matches(':focus-visible')) return;
    state.focused = true;
    updatePlayback(state);
  });
  bind('blur', state => {
    state.focused = false;
    updatePlayback(state);
  });
}
